import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { TYPAL_ICONS } from "./icons";
import { 
  HelpCircle
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface TypalStepProps {
  setCardTypes: (types: string[]) => void;
  setColorIdentity: (colors: string[]) => void;
  handleNextStep: (strategyOverride?: string, colorIdentityOverride?: string[], themesOverride?: string, creatureTypeOverride?: string, cardTypeOverride?: string) => void;
}

const TYPAL_OPTIONS = [
  { id: "artifacts", name: "Artifacts", description: "Use powerful artifacts", type: "artifact", colors: ['W', 'U', 'B'] },
  { id: "enchantments", name: "Enchantments", description: "Weave potent enchantments", type: "enchantment", colors: ['W', 'G'] },
  { id: "lands", name: "Lands", description: "Harness the power of lands", type: "land", colors: ['R', 'G', 'B'] },
  { id: "planeswalkers", name: "Planeswalkers", description: "Command powerful planeswalkers", type: "planeswalker", colors: ['W', 'U', 'B', 'R', 'G'] },
  { id: "spells", name: "Spellcaster", description: "Cast a flurry of instant and sorcery spells", type: "instant sorcery", colors: ['U', 'R'] }
];

export const TypalStep = ({ setCardTypes, setColorIdentity, handleNextStep }: TypalStepProps) => {
  const [selectedTypal, setSelectedTypal] = useState<string>("");

  const handleTypalSelect = (typalId: string) => {
    const typal = TYPAL_OPTIONS.find(t => t.id === typalId);
    if (typal) {
      setSelectedTypal(typalId);
      setCardTypes(typal.type.split(" "));
      setColorIdentity(typal.colors);
      handleNextStep('typal', typal.colors, undefined, undefined, typal.type);
    }
  };

  return (
    <div className="space-y-4 md:space-y-8">
      <div className="text-center">
        <h2 className="text-lg md:text-xl font-bold">Choose Your Typal Focus</h2>
        <p className="text-muted-foreground text-xs md:text-sm max-w-md mx-auto">
          Which card type will you build around?
        </p>
      </div>

      <Card>
        <CardContent className="p-3 md:p-4 space-y-3 md:space-y-4">
          <div className="flex items-center justify-center gap-2 mb-1 md:mb-2">
            <Label className="font-medium text-sm md:text-base">Select a Card Type</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="w-4 h-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">Each card type has its own unique strengths and synergies.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
            {TYPAL_OPTIONS.map((typal) => (
              <Card 
                key={typal.id}
                className={cn(
                  "cursor-pointer transition-all duration-200 hover:scale-105 touch-manipulation",
                  selectedTypal === typal.id && "ring-2 ring-primary bg-primary/5"
                )}
                onClick={() => handleTypalSelect(typal.id)}
              >
                <CardContent className="p-2 md:p-3 text-center space-y-1 md:space-y-2">
                  <div className={cn(
                    "w-8 h-8 md:w-10 md:h-10 mx-auto rounded-full flex items-center justify-center",
                    selectedTypal === typal.id ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                  )}>
                    <div className="w-4 h-4 md:w-6 md:h-6 flex items-center justify-center">
                      {TYPAL_ICONS[typal.id as keyof typeof TYPAL_ICONS]}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1 leading-tight">{typal.name}</h3>
                    <p className="text-xs text-muted-foreground leading-tight">{typal.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
